﻿import { useState } from 'react';
import { useNavigate , Link as RouterLink } from 'react-router';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';

// routes
import { PATH_AUTH, PATH_EXTERNAL } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import { axiosAuthInstance } from 'src/utils/axiosInstances';
import Iconify from '../../../components/Iconify';
import { MfaRequiredError } from 'src/contexts/JWTContext';

// ----------------------------------------------------------------------

type Step = 'email' | 'password';

type LoginFormProps = {
  onStepChange?: (step: Step) => void;
  onError?: (message: string) => void;
};

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export default function LoginForm({ onStepChange, onError }: LoginFormProps) {
  const { login } = useAuth();
  const isMountedRef = useIsMountedRef();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [termsConditions, setTermsConditions] = useState<boolean>(false);

  const emailValid = isValidEmail(email);
  const emailError = emailTouched && !emailValid;

  // CTA disabled logic:
  // - email step: disabled until email is valid and T&C accepted
  const loadingButtonDisabled =
    step === 'email' ? (!emailValid || !termsConditions) : false;

  const goToStep = (s: Step) => {
    setStep(s);
    onStepChange?.(s);
  };

  // ── STEP 1: check email ────────────────────────────────────────────────────
  const handleCheckEmail = async () => {
    setEmailTouched(true);
    if (!emailValid) return;

    setIsLoading(true);
    setError('');

    try {
      const res = await axiosAuthInstance.post('/realm', { email });
      const {data} = res;

      if (data.authMethod === 'PASSWORD') {
        goToStep('password');
      } else {
        window.location.href = data.initiateUrl;
      }
    } catch (err: any) {
      if (isMountedRef.current) {
        setError(err?.response?.data?.message ?? 'Unable to verify email. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── STEP 2: password login ───────────────────────────────────────────────────────
  const handleLogin = async () => {
    setIsLoading(true);
    setError('');

    try {
      await login(email, password);
    } catch (err: any) {
      if (!isMountedRef.current) return;

      if (err instanceof MfaRequiredError) {
        navigate(PATH_AUTH.mfa);
        return;
      }

      const responseData = err?.response?.data;
      if (responseData?.name === 'gen-002') {
        const detail = responseData.details?.find(
          (d: any) => d.target === 'Message'
        );
        const message = detail?.message ?? 'Too many failed attempts. Please try again later.';
        setError(message);
        onError?.(message);
      } else {
        const message =
          err?.response?.data?.message ||
          (typeof err === 'string' ? err : null) ||
          'Username or password are not correct';
        setError(message);
        onError?.(message);
      }
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <Stack spacing={3}>
        {/* Email field – always visible */}
        <Tooltip
          title={emailError ? 'Please enter a valid email address' : ''}
          placement="top"
          arrow
        >
          <span style={{ width: '100%', display: 'block' }}>
            <TextField
              name="email"
              label="Email address"
              type="text"
              inputProps={{ inputMode: 'email' }}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailTouched) setEmailTouched(true);
              }}
              onBlur={() => setEmailTouched(true)}
              disabled={step === 'password'}
              error={emailError}
              sx={{ width: '100%' }}
              InputProps={
                step === 'password'
                  ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => {
                            goToStep('email');
                            setPassword('');
                            setError('');
                            setTermsConditions(false);
                            setEmailTouched(false);
                          }}
                        >
                          <Iconify
                            icon="eva:close-circle-outline"
                            sx={{ fontSize: 20, color: 'text.secondary' }}
                          />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                  : undefined
              }
            />
          </span>
        </Tooltip>

        {/* Password field – only shown in password step */}
        {step === 'password' && (
          <TextField
            name="password"
            label="Password"
            autoFocus
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleLogin(); }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((prev) => !prev)}
                    edge="end"
                  >
                    <Iconify
                      icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      </Stack>

      {/* T&C checkbox – only in email step */}
      {step === 'email' && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 2 }}
        >
          <FormControlLabel
            control={
              <Checkbox
                onChange={(e) => setTermsConditions(e.target.checked)}
                checked={termsConditions}
              />
            }
            label={
              <Typography variant="h4" sx={{ color: 'text.primary' }}>
                I agree with{' '}
                <Link
                  component={RouterLink}
                  sx={{
                    fontWeight: 400,
                    lineHeight: '19.6px',
                    fontSize: '14px',
                    color: '#0627B7',
                  }}
                  to={PATH_EXTERNAL.termsConditions}
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms & Conditions
                </Link>
              </Typography>
            }
          />
        </Stack>
      )}

      {/* CTA button */}
      <LoadingButton
        fullWidth
        loading={isLoading}
        size="large"
        type="button"
        variant="primary"
        disabled={loadingButtonDisabled}
        onClick={step === 'email' ? handleCheckEmail : handleLogin}
        sx={{ mt: 3 }}
      >
        {step === 'email' ? 'Continue' : 'Login'}
      </LoadingButton>
    </>
  );
}